/**
 *
 *  Preferences/ReportsHistory
 *
 */

import React, { useState } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format, parseISO } from 'date-fns'

// GraphQL
import { gql, useMutation } from '@apollo/client'

// Components
import Modal from 'components/Modal'
import QueryTable from 'components/Table/Query'

// Utils
import { COLORS } from 'global-styles'
import { titleize } from 'components/utils'

const color_0_persent = COLORS.danger
const color_30_persent = COLORS.warning
const color_50_persent = '#ffd201'
const color_70_persent = '#b6dc16'
const color_100_persent = COLORS.success

/* eslint-disable indent */
const CustomProgressBar = styled.div`
	${({ progress }) => {
		let progress_color = color_0_persent

		if (progress === 100) progress_color = color_100_persent
		else if (progress >= 70) progress_color = color_70_persent
		else if (progress >= 50) progress_color = color_50_persent
		else if (progress >= 30) progress_color = color_30_persent

		return `
			width: ${progress}%;
			background-color: ${progress_color};
		`
	}}
`
/* eslint-enable indent */

const SEARCH_REPORTS = gql`
	query searchReports(
		$creator_id: ID!
		$page: Int
		$page_size: Int
		$sort: String
		$sort_direction: String
		$filters: JSON
	) {
		search: search_reports(
			creator_id: $creator_id
			page: $page
			page_size: $page_size
			sort: $sort
			sort_direction: $sort_direction
			filters: $filters
		) {
			_total
			results: reports {
				id
				report_type
				status
				status_filterable
				arguments
				obj_exists
				progress
				created_at
				filename
			}
		}
	}
`

const CREATE_REPORT = gql`
	mutation createReport(
		$report_type: String!
		$report_user_id: ID
		$target_user_id: ID
		$description: String
		$search_string: String
		$start_date: String
		$end_date: String
		$job_codes: String
		$zip_codes: String
		$owns_only: Boolean
	) {
		createReport(
			input: {
				report_type: $report_type
				report_user_id: $report_user_id
				target_user_id: $target_user_id
				description: $description
				search_string: $search_string
				start_date: $start_date
				end_date: $end_date
				job_codes: $job_codes
				zip_codes: $zip_codes
				owns_only: $owns_only
			}
		) {
			report {
				id
			}
		}
	}
`

const DELETE_REPORT = gql`
	mutation deleteReport($id: ID) {
		deleteReport(input: { id: $id }) {
			report {
				id
			}
		}
	}
`

const common_tooltip_props = {
	'data-for': 'context-tooltip',
	'data-place': 'bottom',
	'data-effect': 'float',
}

const ActionsCell = ({ report, user_id, openArgs }) => {
	const { report_type, arguments: args } = report

	const [deleteReport, { loading: deleting }] = useMutation(DELETE_REPORT, { variables: { id: report.id } })
	const [createReport] = useMutation(CREATE_REPORT, {
		variables: {
			report_user_id: user_id,
			report_type,
			...args,
		},
	})

	return (
		<div className="text-center">
			<button
				data-tip="Download Report"
				{...common_tooltip_props}
				className="btn btn-sm btn-unstyled p-2 mr-2"
				onClick={() => window.open(`${process.env.API_URL}/reports/${report.id}`, '_self')}
				disabled={!report.obj_exists || deleting}
			>
				<FontAwesomeIcon icon="download" size="lg" className="text-success" />
			</button>
			<button
				data-tip="Generate Report Again"
				{...common_tooltip_props}
				className="btn btn-sm btn-unstyled mr-2"
				onClick={createReport}
				disabled={deleting}
			>
				<FontAwesomeIcon icon="undo" size="lg" className="text-primary" />
			</button>
			<button
				data-tip="View Report Arguments"
				{...common_tooltip_props}
				className="btn btn-sm btn-unstyled p-2 mr-1"
				onClick={() => openArgs({ name: report.filename, args })}
				disabled={deleting}
			>
				<FontAwesomeIcon icon="list" size="lg" className="text-info" />
			</button>
			<button
				data-tip="Remove Report"
				{...common_tooltip_props}
				className="btn btn-sm btn-unstyled p-2"
				onClick={deleteReport}
				disabled={deleting}
			>
				<FontAwesomeIcon icon="trash" size="lg" className="text-danger" />
			</button>
		</div>
	)
}

export const ProgressCell = ({ progress }) => {
	const format_progress = progress >= 0 ? progress : 0

	return (
		<div className="text-center">
			<div className="progress" style={{ height: 12 }}>
				<CustomProgressBar className="progress-bar" progress={progress} />
			</div>
			<span className="small">{`${format_progress}%`}</span>
		</div>
	)
}

const report_types = [
	'contact_spreadsheet_upload',
	'campaign_statistics_report',
	'updated_contacts_report',
	'campaign_attendee_records_report',
	'contact_export_report',
	'campaign_summary',
	'event_summary',
	'appointment_summary',
]

function ReportsHistory({ user_id }) {
	const [selected_report, setSelectedReport] = useState(null)
	const columns = [
		{
			id: 'filename',
			Header: 'File Title',
			accessor: 'filename',
		},
		{
			id: 'report_type',
			Header: 'Report Type',
			accessor: ({ report_type }) => titleize(report_type),
			filter_options: (
				<React.Fragment>
					<option value="">All</option>
					{report_types.map((report, idx) => (
						<option key={idx} value={report}>
							{titleize(report)}
						</option>
					))}
				</React.Fragment>
			),
			filter_key: 'report_type',
			minWidth: 200,
		},
		{
			id: 'status',
			Header: 'Status',
			accessor: ({ status }) => {
				const failed = status === 'Failed'
				const pending = status === 'Pending'
				const completed = status === 'Completed'

				let status_class

				if (failed) {
					status_class = 'danger'
				} else if (pending) {
					status_class = 'warning'
				} else if (completed) {
					status_class = 'success'
				} else {
					status_class = 'primary'
				}

				return (
					<div className="d-flex justify-content-center">
						<span className={`small py-1 px-2 rounded text-white bg-${status_class}`}>
							{completed || pending || failed ? status : 'Processing'}
						</span>
					</div>
				)
			},
			filter_key: 'status',
			filter_options: (
				<React.Fragment>
					<option value="">All</option>
					<option value="completed">Completed</option>
					<option value="failed">Failed</option>
					<option value="processing">Processing</option>
				</React.Fragment>
			),
			minWidth: 150,
		},
		{
			id: 'progress',
			Header: 'Progress',
			accessor: ({ progress }) => <ProgressCell progress={progress} />,
			minWidth: 200,
		},
		{
			id: 'created_at',
			Header: 'Creation Date',
			accessor: (r) => format(parseISO(r.created_at), 'MMM do, yyyy h:mma'),
			minWidth: 200,
		},
		{
			id: 'actions',
			accessor: (row) => <ActionsCell report={row} user_id={user_id} openArgs={setSelectedReport} />,
			sortable: false,
			minWidth: 200,
			withTooltip: true,
		},
	]

	return (
		<React.Fragment>
			<QueryTable
				query={SEARCH_REPORTS}
				variables={{ creator_id: user_id }}
				columns={columns}
				channels={[user_id]}
				data_selector="search_reports"
			/>
			<Modal show={selected_report} modalDidClose={() => setSelectedReport(null)} style={{ width: 550 }}>
				<h5 className="mb-3">
					Arguments for Report: <b>{selected_report?.name}</b>
				</h5>
				<table className="table table-bordered m-0">
					<thead>
						<tr>
							<th scope="col">Argument</th>
							<th scope="col">Value</th>
						</tr>
					</thead>
					<tbody>
						{selected_report?.args &&
							Object.keys(selected_report.args)?.map((arg_key, idx) => (
								<tr key={idx}>
									<td>{titleize(arg_key)}</td>
									<td>{selected_report.args[arg_key]}</td>
								</tr>
							))}
					</tbody>
				</table>
			</Modal>
		</React.Fragment>
	)
}

ReportsHistory.propTypes = {
	user_id: PropTypes.string,
}

export default ReportsHistory
